/**
 * The guide's pointer on screen-shared tours. Before the tour changes screen, it glides to the link it's
 * opening, rings it and shows a click, so prospects can follow along. After the change, it rings the part of
 * the page the guide is explaining.
 */

const SVG_NS = "http://www.w3.org/2000/svg";
const RING_PADDING = 6;

export interface PointerTiming {
  /** Glide from wherever the pointer is to the link it's about to click. */
  moveMs: number;
  /** Rest on the highlighted link, so viewers see what's about to be clicked. */
  holdMs: number;
  /** The click itself. */
  clickMs: number;
  /** How long the highlight stays around the part of the page being explained. */
  spotlightMs: number;
}

export const POINTER_TIMING: PointerTiming = { moveMs: 700, holdMs: 700, clickMs: 300, spotlightMs: 4000 };

export interface GuidePointer {
  /** Resolves once the click has shown; the caller then changes the screen. */
  click(target: Element): Promise<void>;
  spotlight(target: Element): void;
  destroy(): void;
}

function isShown(element: Element, view: Window | null): boolean {
  for (let node: Element | null = element; node; node = node.parentElement) {
    if (node.hasAttribute("hidden") || view?.getComputedStyle(node).display === "none") {
      return false;
    }
  }
  return true;
}

/** The visible link a person would click to get to `path` (or to `#anchor`, on the page they're already on). */
export function findClickTarget(
  doc: Document,
  path: string,
  anchor: string | null,
  currentPath: string,
): HTMLAnchorElement | null {
  const href = anchor && path === currentPath ? `#${anchor}` : path;
  for (const link of doc.querySelectorAll<HTMLAnchorElement>("a[href]")) {
    if (link.getAttribute("href") === href && isShown(link, doc.defaultView)) {
      return link;
    }
  }
  return null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function arrow(doc: Document): SVGSVGElement {
  const svg = doc.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 32");
  svg.setAttribute("width", "24");
  svg.setAttribute("height", "32");
  const shape = doc.createElementNS(SVG_NS, "path");
  shape.setAttribute("d", "M2 2 L2 26 L8.5 20 L13 30 L17.5 28 L13 18.5 L22 18.5 Z");
  shape.setAttribute("fill", "#16181d");
  shape.setAttribute("stroke", "#ffffff");
  shape.setAttribute("stroke-width", "2");
  shape.setAttribute("stroke-linejoin", "round");
  svg.append(shape);
  return svg;
}

export function createGuidePointer(doc: Document, timing: PointerTiming = POINTER_TIMING): GuidePointer {
  const view = doc.defaultView ?? window;
  const layer = doc.createElement("div");
  layer.className = "tour-pointer";
  layer.setAttribute("aria-hidden", "true");
  const ring = doc.createElement("div");
  ring.className = "tour-ring";
  const ripple = doc.createElement("div");
  ripple.className = "tour-ripple";
  const cursor = doc.createElement("div");
  cursor.className = "tour-cursor";
  cursor.style.transitionDuration = `${timing.moveMs}ms`;
  cursor.append(arrow(doc));
  layer.append(ring, ripple, cursor);
  doc.body.append(layer);

  let tracked: Element | null = null;
  let frame = 0;
  let fadeTimer = 0;
  let point = { x: view.innerWidth * 0.6, y: view.innerHeight * 0.6 };

  function moveCursor(x: number, y: number) {
    point = { x, y };
    cursor.style.transform = `translate(${x}px, ${y}px)`;
  }

  function hideRing() {
    view.cancelAnimationFrame(frame);
    tracked = null;
    ring.classList.remove("is-visible");
  }

  // Re-measured every frame, so the ring stays on its target while the page scrolls.
  function follow() {
    if (!tracked?.isConnected) {
      hideRing();
      return;
    }
    const rect = tracked.getBoundingClientRect();
    ring.style.transform = `translate(${rect.left - RING_PADDING}px, ${rect.top - RING_PADDING}px)`;
    ring.style.width = `${rect.width + RING_PADDING * 2}px`;
    ring.style.height = `${rect.height + RING_PADDING * 2}px`;
    frame = view.requestAnimationFrame(follow);
  }

  function showRing(target: Element) {
    view.clearTimeout(fadeTimer);
    view.cancelAnimationFrame(frame);
    tracked = target;
    ring.classList.add("is-visible");
    follow();
  }

  moveCursor(point.x, point.y);

  return {
    async click(target) {
      const rect = target.getBoundingClientRect();
      showRing(target);
      moveCursor(rect.left + Math.min(rect.width / 2, 36), rect.top + rect.height / 2);
      await sleep(timing.moveMs + timing.holdMs);

      ripple.style.transform = `translate(${point.x}px, ${point.y}px)`;
      ripple.classList.remove("is-clicking");
      ripple.getBoundingClientRect(); // restart the ripple animation
      ripple.classList.add("is-clicking");
      cursor.classList.add("is-pressed");
      await sleep(timing.clickMs);
      cursor.classList.remove("is-pressed");
      hideRing();
    },
    spotlight(target) {
      showRing(target);
      fadeTimer = view.setTimeout(hideRing, timing.spotlightMs);
    },
    destroy() {
      view.clearTimeout(fadeTimer);
      hideRing();
      layer.remove();
    },
  };
}
